import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import * as Moment from 'moment';
import { DateParser } from '@fnv-core/parsing/date/DateParser';
import { FormControlLabelAndPopover } from '../FormControlLabel';
import { ValidationErrorsMessages } from '../FormValidation';
import { CultureHelper } from './../../helpers/CultureHelper';
import { format, parse, isValid } from "date-fns";

// Function to render a CalendarControl (Date Picker)
export const CalendarControl = ({ field, handleChange, handleBlur, formDataEntry, errors } : ControlParameters) : JSX.Element => {
  const [dataDate, setDataDate] = useState(formDataEntry && formDataEntry.value ? new DateParser().parse(formDataEntry.value) : null);
  const [date, setDate] = useState(formDataEntry && formDataEntry.value ? new DateParser().parse(formDataEntry.value) : null);
  const [input, setInput] = useState(null);
  const [isFocused, setIsFocused] = useState(false);

  const ref = useRef<DatePicker>();
  const [locale, setLocale] = useState(null);
  const dateFormat = "dd-MM-yyyy";
  
  useEffect(() => {
    const fetchCulture = async () => {
      const currentCulture = await CultureHelper.getCurrentCulture();
      setLocale(currentCulture.cultureImport);
    };

    fetchCulture();
  }, []);

  useEffect(() => {
    if(isFocused) {
      setInput(null);
      setDate(null)
    }
    else {
      setInput(parse(dataDate?.toString(), dateFormat, new Date()))
      setDate(dataDate ? new DateParser().parse(dataDate) : null)
    }
  }, [isFocused]);

  useEffect(() => {
    if (ref?.current && isValid(dataDate) && errors?.length == 0) {
      ref.current.setOpen(false);
    }
  }, [errors, dataDate]);

  useEffect(() => {
    const parsedDate = parse(input, dateFormat, new Date());
    if (isValid(parsedDate) && isValidDateString(input, dateFormat)) {
      handleDateChange(new DateParser().parse(input));
    }
  }, [input]);

  useEffect(() => {
    if(!isValid(date) || (input?.length ?? 0) > 0 && !isValidDateString(input, dateFormat)) {
      return;
    }

    handleChange({
      target: {
        name: field["@column"],
        value: date
      }
    });

    setDataDate(date);
  }, [date]);

  const handleDateChange = data => {
    setDate(data);
  };

  function formatPartialDateString(input, format) {
    input = input.replace(/\D/g, "");
  
    const formatParts = format.split(/[-/.\s]/);
    const separators = format.match(/[-/.\s]/g);
    let formattedDate = "";
    let currentPosition = 0;
  
    formatParts.forEach((part, index) => {
      const partLength = part.length;
      const partValue = input.slice(currentPosition, currentPosition + partLength);
      currentPosition += partLength;
  
      formattedDate += partValue;
  
      if (index < formatParts.length - 1 && currentPosition < input.length) {
        formattedDate += separators[index] || "";
      }
    });
  
    return formattedDate;
  }

  function isValidDateString(dateString, format) {
    const regex = createDateRegex(format);
    return regex.test(dateString);
  }

  function createDateRegex(format) {
    const regexString = format
      .replace(/dd/g, '(\\d{2})')
      .replace(/MM/g, '(\\d{2})')
      .replace(/yyyy/g, '(\\d{4})')
      .replace(/-/g, '-')
      .replace(/\//g, '/')
      .replace(/\./g, '\\.')
      .replace(/\s/g, '\\s');
  
    return new RegExp(`^${regexString}$`);
  }
  
  const handleOnChangeRaw = (event) => {
    let {value} = event.target;

    const diffIndex = findDifferenceIndex(input ?? '', value);
    if (diffIndex !== -1) {
      value = replaceCharacterAt(value, diffIndex, value[diffIndex]);
    }

    const formattedValue = formatPartialDateString(value, dateFormat);
    setInput(formattedValue);
  };

  const handleDateBlur = (event) => {
    if(event?.target != null && Array.from(event.target.classList).findIndex((element:string) => element.includes("onclickoutside")) == -1) {
      if (!isValidDateString(input, dateFormat)) {
        setInput(date != null ? format(date, dateFormat) : "");
      }
      setIsFocused(false);
      handleBlur({
        target: {
          name: field["@column"]
        }
      });
    }
  };

  const findDifferenceIndex = (str1, str2) => {
    for (let i = 0; i < Math.max(str1.length, str2.length); i++) {
      if (str1[i] !== str2[i]) {
        return i;
      }
    }
    return -1;
  };

  const replaceCharacterAt = (str, index, replacement) => {
    if (index < 0 || index >= str.length) return str; // safeguard for out-of-bounds index
    return str.slice(0, index) + replacement + str.slice(index + 1);
  };

  return (
    <div className={`form-group ${field.properties?.fieldcssclass ?? ''}`} key={field["@guid"]}>
      <FormControlLabelAndPopover field={field} loading={formDataEntry.loading} />
      <div className="input-group">
        <label className="react-datepicker__label">
          {locale && (
            <DatePicker
              name={field["@column"]}
              ref={ref}
              dateFormat={dateFormat}
              className="form-control react-datepicker__input"
              value={input}
              selected={date}
              onChangeRaw={handleOnChangeRaw}
              onChange={handleDateChange}
              maxDate={Moment().toDate()}
              onFocus={() => setIsFocused(true)}
              onBlur={handleDateBlur}
              maxMonth={12}
              showYearDropdown={true}
              showMonthDropdown={true}
              autoComplete="off"
              locale={locale}
              isClearable={false}
              adjustDateOnChange={true}
              scrollableYearDropdown={true}
              yearDropdownItemNumber={100}
              dropdownMode="scroll"
            />
          )}
        </label>
        <div className="input-group-append form-control--is-valid-indicator">
          <div className="input-group-text">
            <span className="icon icon-checkmark" />
          </div>
        </div>
        <ValidationErrorsMessages keyGuid={field["@guid"]} errors={errors} />
      </div>
    </div>
  );
};